import React, { useState, useEffect, useCallback } from "react";
import Editor from "@monaco-editor/react";

// Default payloads come from GoogleAdsAI::Generator.default_payloads_for_ui (API response).
// No client-side copies — single source of truth is generator.rb.
const STAGE_TABS = [
  { id: "stage1", label: "Stage 1: Marketing Plan" },
  { id: "stage2", label: "Stage 2: Campaign Plan" },
  { id: "stage3a", label: "Stage 3a: Structure" },
  { id: "stage3b", label: "Stage 3b: Explanations" },
  { id: "stage4", label: "Stage 4: Payload Conversion" }
];

const EMPTY_JSON = "{}";

const getPayloadFormat = (payloadString) => {
  try {
    const payload = JSON.parse(payloadString);
    if (payload.input || payload.text?.format) {
      return { format: "Responses API", color: "#059669", bg: "#d1fae5" };
    }
    if (payload.messages || payload.response_format) {
      return { format: "Chat Completions (legacy)", color: "#d97706", bg: "#fef3c7" };
    }
    return { format: "Unknown", color: "#6b7280", bg: "#f3f4f6" };
  } catch {
    return { format: "Invalid JSON", color: "#dc2626", bg: "#fef2f2" };
  }
};

/**
 * Google Ads AI Configuration tab. Default payloads come from generator.rb via the API
 * (GoogleAdsAI::Generator.default_payloads_for_ui). No client-side payload copies.
 */
function AIConfigurationTab() {
  const [activeStage, setActiveStage] = useState("stage1");
  const [payloads, setPayloads] = useState({
    stage1: EMPTY_JSON,
    stage2: EMPTY_JSON,
    stage3a: EMPTY_JSON,
    stage3b: EMPTY_JSON,
    stage4: EMPTY_JSON
  });
  const [originalPayloads, setOriginalPayloads] = useState({});
  const [backendDefaults, setBackendDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [saveMessage, setSaveMessage] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch("/api/google_ads_ai/settings", {
        headers: { Accept: "application/json" }
      });

      if (response.ok) {
        const data = await response.json();
        const defs = data.defaults || {};
        setBackendDefaults({
          stage1: defs.stage1 ?? defs["stage1"] ?? EMPTY_JSON,
          stage2: defs.stage2 ?? defs["stage2"] ?? EMPTY_JSON,
          stage3a: defs.stage3a ?? defs["stage3a"] ?? EMPTY_JSON,
          stage3b: defs.stage3b ?? defs["stage3b"] ?? EMPTY_JSON,
          stage4: defs.stage4 ?? defs["stage4"] ?? EMPTY_JSON
        });
        const newPayloads = {
          stage1: data.stage1_api_payload || data.defaults?.stage1 || defs.stage1 || defs["stage1"] || EMPTY_JSON,
          stage2: data.stage2_api_payload || data.defaults?.stage2 || defs.stage2 || defs["stage2"] || EMPTY_JSON,
          stage3a: data.stage3a_api_payload || data.defaults?.stage3a || defs.stage3a || defs["stage3a"] || EMPTY_JSON,
          stage3b: data.stage3b_api_payload || data.defaults?.stage3b || defs.stage3b || defs["stage3b"] || EMPTY_JSON,
          stage4: data.stage4_api_payload || data.defaults?.stage4 || defs.stage4 || defs["stage4"] || EMPTY_JSON
        };
        setPayloads(newPayloads);
        setOriginalPayloads(newPayloads);
      }
    } catch (error) {
      console.error("Error fetching Google Ads AI settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const validateJSON = useCallback((value, stage) => {
    try {
      JSON.parse(value);
      setValidationErrors((prev) => ({ ...prev, [stage]: null }));
      return true;
    } catch (e) {
      setValidationErrors((prev) => ({ ...prev, [stage]: e.message }));
      return false;
    }
  }, []);

  const handleEditorChange = (value, stage) => {
    setPayloads((prev) => ({ ...prev, [stage]: value }));
    validateJSON(value, stage);
    setSaveMessage(null);
  };

  const handleSave = async () => {
    const allValid = STAGE_TABS.every((tab) => validateJSON(payloads[tab.id], tab.id));
    if (!allValid) {
      setSaveMessage({ type: "error", text: "Please fix JSON validation errors before saving." });
      return;
    }
    setSaving(true);
    setSaveMessage(null);
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
      const response = await fetch("/api/google_ads_ai/settings", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrf || ""
        },
        body: JSON.stringify({
          settings: {
            stage1_api_payload: payloads.stage1,
            stage2_api_payload: payloads.stage2,
            stage3a_api_payload: payloads.stage3a,
            stage3b_api_payload: payloads.stage3b,
            stage4_api_payload: payloads.stage4
          }
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setOriginalPayloads({ ...payloads });
        setSaveMessage({ type: "success", text: "Settings saved successfully!" });
      } else {
        setSaveMessage({ type: "error", text: data.error || "Failed to save settings." });
      }
    } catch (error) {
      setSaveMessage({ type: "error", text: "Error saving settings: " + error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = () => {
    const defaultValue = backendDefaults[activeStage];
    if (defaultValue) {
      setPayloads((prev) => ({ ...prev, [activeStage]: defaultValue }));
      setValidationErrors((prev) => ({ ...prev, [activeStage]: null }));
      setSaveMessage(null);
    }
  };

  const handleResetAll = () => {
    if (window.confirm("Reset all stages to default? This will discard any unsaved changes.")) {
      setPayloads({ ...backendDefaults });
      setValidationErrors({});
      setSaveMessage(null);
    }
  };

  const hasUnsavedChanges = () =>
    STAGE_TABS.some((tab) => payloads[tab.id] !== originalPayloads[tab.id]);

  if (loading) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        Loading AI configuration…
      </div>
    );
  }

  const formatInfo = getPayloadFormat(payloads[activeStage]);

  return (
    <div className="ai-configuration-tab">
      <div
        style={{
          padding: "12px 16px",
          background: "#eff6ff",
          border: "1px solid #bfdbfe",
          borderRadius: "6px",
          marginBottom: "16px",
          fontSize: "13px",
          color: "#1e40af"
        }}
      >
        <strong>AI Prompt Configuration</strong>
        <p style={{ margin: "8px 0 0 0" }}>
          Customize the prompts used for each stage of AI-powered Google Ads (Search) campaign generation.
          Use <code style={{ background: "#dbeafe", padding: "2px 4px", borderRadius: "3px" }}>{"{{VARIABLE_NAME}}"}</code> for
          dynamic values like <code style={{ background: "#dbeafe", padding: "2px 4px", borderRadius: "3px" }}>{"{{EVENT_DETAILS}}"}</code>,
          <code style={{ background: "#dbeafe", padding: "2px 4px", borderRadius: "3px" }}>{"{{MARKETING_PLAN}}"}</code>,{" "}
          <code style={{ background: "#dbeafe", padding: "2px 4px", borderRadius: "3px" }}>{"{{CAMPAIGN_PLAN}}"}</code>, etc.
        </p>
      </div>

      <div
        style={{
          display: "flex",
          gap: "4px",
          marginBottom: "16px",
          borderBottom: "1px solid #e5e7eb"
        }}
      >
        {STAGE_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveStage(tab.id)}
            style={{
              padding: "10px 16px",
              border: "none",
              background: "transparent",
              borderBottom: activeStage === tab.id ? "2px solid #3b82f6" : "2px solid transparent",
              color: activeStage === tab.id ? "#3b82f6" : "#6b7280",
              fontSize: "13px",
              fontWeight: activeStage === tab.id ? "600" : "400",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
          >
            {tab.label}
            {validationErrors[tab.id] && (
              <span style={{ color: "#dc2626", marginLeft: "6px" }}>!</span>
            )}
          </button>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "8px"
        }}
      >
        <span
          style={{
            padding: "4px 10px",
            borderRadius: "12px",
            fontSize: "11px",
            fontWeight: "500",
            background: formatInfo.bg,
            color: formatInfo.color
          }}
        >
          Format: {formatInfo.format}
        </span>
      </div>

      <div
        style={{
          border: validationErrors[activeStage] ? "2px solid #dc2626" : "1px solid #e5e7eb",
          borderRadius: "6px",
          overflow: "hidden",
          marginBottom: "12px"
        }}
      >
        <Editor
          height="350px"
          language="json"
          theme="vs-light"
          value={payloads[activeStage]}
          onChange={(value) => handleEditorChange(value, activeStage)}
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            lineNumbers: "on",
            scrollBeyondLastLine: false,
            wordWrap: "on",
            automaticLayout: true,
            tabSize: 2,
            formatOnPaste: true
          }}
        />
      </div>

      {validationErrors[activeStage] && (
        <div
          style={{
            padding: "10px 12px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: "6px",
            marginBottom: "12px",
            fontSize: "13px",
            color: "#dc2626"
          }}
        >
          <strong>JSON Error:</strong> {validationErrors[activeStage]}
        </div>
      )}

      {saveMessage && (
        <div
          style={{
            padding: "10px 12px",
            background: saveMessage.type === "success" ? "#f0fdf4" : "#fef2f2",
            border: `1px solid ${saveMessage.type === "success" ? "#bbf7d0" : "#fecaca"}`,
            borderRadius: "6px",
            marginBottom: "12px",
            fontSize: "13px",
            color: saveMessage.type === "success" ? "#166534" : "#dc2626"
          }}
        >
          {saveMessage.text}
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: "12px",
          borderTop: "1px solid #e5e7eb"
        }}
      >
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            type="button"
            onClick={handleResetToDefault}
            style={{
              padding: "8px 14px",
              background: "#fff",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "13px",
              cursor: "pointer"
            }}
          >
            Reset Stage to Default
          </button>
          <button
            type="button"
            onClick={handleResetAll}
            style={{
              padding: "8px 14px",
              background: "#fff",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "13px",
              cursor: "pointer",
              color: "#6b7280"
            }}
          >
            Reset All
          </button>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || Object.values(validationErrors).some(Boolean)}
          style={{
            padding: "8px 20px",
            background: saving ? "#9ca3af" : "#3b82f6",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            fontSize: "13px",
            fontWeight: "500",
            cursor: saving ? "not-allowed" : "pointer"
          }}
        >
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>

      {hasUnsavedChanges() && !saveMessage && (
        <div style={{ marginTop: "12px", fontSize: "12px", color: "#f59e0b", fontStyle: "italic" }}>
          You have unsaved changes.
        </div>
      )}
    </div>
  );
}

export default AIConfigurationTab;
